import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../store';
import { useNavigate } from 'react-router-dom';
import { GlassButton } from '../components/GlassUI';
import { hasPlanFeature, PLAN_FEATURES } from '../config/plans';
import { customerService } from '../services/api';
import {
  FaHome,
  FaUserPlus,
  FaUsers,
  FaBoxOpen,
  FaShoppingCart,
  FaTools,
  FaGem,
  FaUser,
  FaCreditCard,
  FaSignOutAlt,
  FaCrown,
  FaLock,
  FaChevronUp,
  FaChevronDown,
  FaExclamationTriangle,
} from 'react-icons/fa';

interface NavbarProps {
  currentPage: string;
  onPageChange: (page: string) => void;
  onClose?: () => void;
}

const navItems = [
  { id: 'dashboard', label: 'Dashboard', icon: FaHome, feature: PLAN_FEATURES.DASHBOARD },
  { id: 'onboarding', label: 'Customer Onboarding', icon: FaUserPlus, feature: PLAN_FEATURES.ONBOARDING },
  { id: 'customers', label: 'Customers', icon: FaUsers, feature: PLAN_FEATURES.CUSTOMERS },
  { id: 'orders', label: 'Orders', icon: FaBoxOpen, feature: PLAN_FEATURES.ORDERS },
  { id: 'gold-sale', label: 'New Sale', icon: FaShoppingCart, feature: PLAN_FEATURES.SALE },
  { id: 'repairs', label: 'Repairs', icon: FaTools, feature: PLAN_FEATURES.REPAIRS },
  { id: 'old-gold', label: 'Old Gold', icon: FaGem, feature: PLAN_FEATURES.OLD_GOLD },
  { id: 'profile', label: 'Profile', icon: FaUser, feature: PLAN_FEATURES.DASHBOARD },
  { id: 'subscription', label: 'CRM Plan', icon: FaCreditCard, feature: PLAN_FEATURES.DASHBOARD },
];

export const Navbar: React.FC<NavbarProps> = ({ currentPage, onPageChange, onClose }) => {
  const { logout, user, subscription } = useAuthStore();
  const navigate = useNavigate();
  const [minimized, setMinimized] = useState(false);
  const [customerCount, setCustomerCount] = useState<number | null>(null);

  useEffect(() => {
    let active = true;
    customerService
      .getAll(1, 1)
      .then((res) => {
        if (active) setCustomerCount(res.data.pagination?.total ?? null);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (id: string) =>
    currentPage === id || (id === 'orders' && currentPage === 'order-detail');

  const planId = subscription?.plan;
  const visibleItems = navItems.filter((item) => hasPlanFeature(planId, item.feature));
  const lockedCount = navItems.filter((item) => !hasPlanFeature(planId, item.feature)).length;

  const maxCustomers = subscription?.maxCustomers;
  const unlimited = !!maxCustomers && maxCustomers >= 999999;

  const daysLeft = (() => {
    const end = subscription?.endDate || subscription?.trialEnd;
    if (!end) return null;
    const diff = Math.ceil((new Date(end).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    return diff >= 0 ? diff : null;
  })();

  const status = subscription?.status;
  const isTrial = status === 'TRIAL';
  const isExpired = status === 'EXPIRED' || status === 'CANCELLED';
  // Critical = expired. Warning = free trial or plan expiring within 30 days.
  const isCritical = isExpired;
  const isWarning = !isCritical && (isTrial || (daysLeft !== null && daysLeft <= 30));
  const isUrgent = isCritical || isWarning;

  const usagePct =
    customerCount !== null && maxCustomers && !unlimited
      ? Math.min(100, Math.round((customerCount / maxCustomers) * 100))
      : null;

  const usageColor =
    usagePct !== null && usagePct >= 90
      ? 'bg-red-500'
      : usagePct !== null && usagePct >= 70
      ? 'bg-amber-500'
      : 'bg-emerald-500';

  return (
    <div className="flex flex-col h-full bg-white border-r border-slate-200/70">
      {/* Brand */}
      <div className="px-5 py-5 border-b border-slate-100">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-slate-900 to-slate-700 flex items-center justify-center shadow-sm">
            <FaGem className="text-amber-400 text-base" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-900 leading-tight tracking-tight">ApniCRM</h1>
            <p className="text-[11px] text-slate-500 leading-tight">Jewelry Store Management</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-0.5">
        <p className="px-2 pb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
          Menu
        </p>
        {visibleItems.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => {
              onPageChange(id);
              onClose?.();
            }}
            className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-left transition-all duration-150 ${
              isActive(id)
                ? 'bg-slate-900 text-white shadow-sm shadow-slate-900/20'
                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
            }`}
          >
            <Icon className={`flex-shrink-0 text-[15px] ${isActive(id) ? 'text-amber-400' : ''}`} />
            <span className="truncate text-sm font-medium">{label}</span>
            {id === 'subscription' && status === 'ACTIVE' && (
              <span className="ml-auto flex-shrink-0">
                <FaCrown className="text-amber-400 text-xs" />
              </span>
            )}
          </button>
        ))}

        {/* Upgrade hint for locked modules */}
        {lockedCount > 0 && (
          <button
            onClick={() => {
              onPageChange('subscription');
              onClose?.();
            }}
            className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-left mt-3 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 text-amber-800 hover:bg-amber-100 transition-all"
          >
            <FaLock className="flex-shrink-0 text-[13px]" />
            <span className="truncate text-sm font-semibold">{lockedCount} locked — Upgrade</span>
          </button>
        )}
      </nav>

      {/* User row (slim, always visible) */}
      <div className="px-4 pb-2">
        <div className="flex items-center gap-2.5 rounded-xl px-1 py-1">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-slate-800 text-sm font-semibold truncate">{user?.name}</p>
            <p className="text-slate-400 text-[11px] truncate">{user?.email}</p>
          </div>
          <button
            onClick={handleLogout}
            title="Logout"
            className="w-8 h-8 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 flex items-center justify-center shrink-0 transition-colors"
          >
            <FaSignOutAlt className="text-sm" />
          </button>
        </div>
      </div>

      {/* Plan widget — only plan · days left · usage, collapsible, highlights when urgent */}
      <div className="px-4 pb-4">
        {!minimized ? (
          <div
            className={`rounded-2xl border p-3.5 transition-all ${
              isCritical
                ? 'border-red-300 bg-red-50'
                : isWarning
                ? 'border-amber-300 bg-amber-50/70'
                : 'border-slate-200 bg-slate-50'
            }`}
          >
            {/* Header row */}
            <div className="flex items-center gap-2">
              <div
                className={`flex items-center gap-1.5 text-xs font-bold px-2 py-1 rounded-lg ${
                  isCritical
                    ? 'bg-red-600 text-white'
                    : isWarning
                    ? 'bg-amber-500 text-white'
                    : 'bg-slate-900 text-amber-400'
                }`}
              >
                <FaCrown className="text-[10px]" />
                {planId || 'FREE'}
              </div>
              <span className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">
                Yearly Plan
              </span>
              <div className="ml-auto flex items-center gap-1">
                <button
                  onClick={() => setMinimized(true)}
                  title="Minimise plan card"
                  className="w-6 h-6 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-200/70 flex items-center justify-center transition-colors"
                >
                  <FaChevronDown className="text-[10px]" />
                </button>
              </div>
            </div>

            {/* Days left */}
            <div className="mt-2.5 flex items-center justify-between">
              <p className="text-[11px] text-slate-500">
                {isExpired ? 'Plan ended' : isTrial ? 'Trial ends in' : 'Expires in'}
              </p>
              <p
                className={`text-sm font-bold ${
                  isCritical ? 'text-red-600' : isWarning ? 'text-amber-700' : 'text-slate-900'
                }`}
              >
                {isExpired ? (
                  '—'
                ) : daysLeft !== null ? (
                  <>
                    {daysLeft} <span className="text-[11px] font-semibold text-slate-500">days</span>
                  </>
                ) : (
                  '—'
                )}
              </p>
            </div>

            {/* Usage */}
            {usagePct !== null && (
              <div className="mt-2">
                <div className="flex items-center justify-between text-[11px] mb-1">
                  <p className="text-slate-500">
                    Usage · {customerCount?.toLocaleString()}
                    {!unlimited && maxCustomers ? ` / ${maxCustomers.toLocaleString()}` : ''}
                  </p>
                  <p className={`font-semibold ${usagePct >= 90 ? 'text-red-600' : usagePct >= 70 ? 'text-amber-600' : 'text-emerald-600'}`}>
                    {usagePct}%
                  </p>
                </div>
                <div className="h-1.5 w-full bg-slate-200 rounded-full overflow-hidden">
                  <div
                    className={`h-1.5 rounded-full ${usageColor} transition-all`}
                    style={{ width: `${usagePct}%` }}
                  />
                </div>
              </div>
            )}

            {/* CTA — red only when expired; gold nudge for trial / expiring soon */}
            {isCritical && (
              <GlassButton
                variant="danger"
                className="w-full mt-3 text-xs py-2"
                onClick={() => {
                  onPageChange('subscription');
                  onClose?.();
                }}
              >
                <FaExclamationTriangle className="text-[11px]" />
                Renew — Yearly Plan
              </GlassButton>
            )}
            {!isCritical && isWarning && (
              <GlassButton
                variant="gold"
                className="w-full mt-3 text-xs py-2"
                onClick={() => {
                  onPageChange('subscription');
                  onClose?.();
                }}
              >
                <FaCrown className="text-[11px]" />
                {isTrial ? 'Choose Yearly Plan' : 'Renew Yearly Plan'}
              </GlassButton>
            )}
            {!isUrgent && daysLeft !== null && daysLeft <= 90 && (
              <button
                onClick={() => {
                  onPageChange('subscription');
                  onClose?.();
                }}
                className="w-full mt-2.5 text-[11px] font-semibold text-slate-500 hover:text-slate-800 underline underline-offset-2 transition-colors"
              >
                View renewal options
              </button>
            )}
          </div>
        ) : (
          /* Minimised chip — one line, one click to expand */
          <button
            onClick={() => setMinimized(false)}
            className={`w-full flex items-center gap-2 rounded-xl border px-3 py-2 transition-all ${
              isCritical
                ? 'border-red-300 bg-red-50 hover:bg-red-100'
                : isWarning
                ? 'border-amber-300 bg-amber-50/70 hover:bg-amber-100'
                : 'border-slate-200 bg-slate-50 hover:bg-slate-100'
            }`}
            title="Expand plan details"
          >
            <FaCrown
              className={`text-xs ${isCritical ? 'text-red-500' : isWarning ? 'text-amber-500' : 'text-slate-400'}`}
            />
            <span
              className={`text-xs font-bold ${
                isCritical ? 'text-red-700' : isWarning ? 'text-amber-800' : 'text-slate-800'
              }`}
            >
              {planId || 'FREE'}
            </span>
            <span
              className={`text-[11px] font-semibold ${
                isCritical ? 'text-red-600' : isWarning ? 'text-amber-700' : 'text-slate-500'
              }`}
            >
              {isExpired ? '· expired' : daysLeft !== null ? `· ${daysLeft}d left` : ''}
            </span>
            {usagePct !== null && (
              <span className="ml-auto flex items-center gap-1">
                <span
                  className={`h-1.5 w-10 rounded-full overflow-hidden ${
                    isCritical ? 'bg-red-200' : isWarning ? 'bg-amber-200' : 'bg-slate-200'
                  }`}
                >
                  <span
                    className={`block h-full rounded-full ${
                      isCritical ? 'bg-red-500' : isWarning ? 'bg-amber-500' : usageColor
                    }`}
                    style={{ width: `${usagePct}%` }}
                  />
                </span>
                <FaChevronUp className="ml-auto text-[10px] text-slate-400" />
              </span>
            )}
            {!usagePct && <FaChevronUp className="ml-auto text-[10px] text-slate-400" />}
          </button>
        )}
      </div>
    </div>
  );
};

export default Navbar;